import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";

interface IRectangularForm {
  sideA: string,
  sideB: string,
  diagonal?: string,
}
interface IRectangularResults {
  area?: string;
}

export class RectangularVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.formLabelsForExport = {
      sideA: "Side 'a'",
      sideB: "Side 'b'",
      diagonal: 'Diagonal',
    }
    this.resultLabelsForExport = {
      area: 'Area (sq units)',
    }
  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable rectangularForm: IRectangularForm = {
    sideA: this.defaultValues?.sideA,
    sideB: this.defaultValues?.sideB,
    diagonal: this.defaultValues?.diagonal,
  }
  @observable rectangularResults: IRectangularResults = {
    area: '12.00'
  }
  @observable rectangularFormInputSnapshot: IRectangularForm | undefined = undefined
  @observable hasResults: boolean = false;
  @observable rectangularControl: number = 1;
  @observable formValidation = {
    hasError: true,
    sideA: false,
    sideB: false,
    diagonal: false,
    formErrors: {
      sideA: '',
      sideB: '',
      diagonal: ''
    },
  };

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

  @action.bound rectangularErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  get diagonalControl(): boolean {
    return this.rectangularControl === 0;
  }
  get sideControl(): boolean {
    return this.rectangularControl === 1;
  }
  @action.bound setFixedValue(name, fixed, precision?) {
    const event1 = {
     target: {
     name: name,
     value: this.roundingHelperString(fixed, 2)
     }
     }
    this.handleFormChange(event1, true);
     }
  @action.bound async handleFormChange(event, within?: boolean) {
    const { name, value } = event.target;
    if (!within && value.length > 7) {
      return
    }
    switch (name) {
      case 'sideA':
        this.rectangularForm.sideA = value;
        break;
      case 'sideB':
        this.rectangularForm.sideB = value;
        break;
      case 'diagonal':
        this.rectangularForm.diagonal = value;
        break;
      case 'diagonalControl':
        this.rectangularControl = 0
        break;
      case 'sideControl':
        this.rectangularControl = 1
        break;
    }
  }
  @action.bound async validateInput(name, value) {
    const val = this.roundingHelperNumber(parseFloat(value), 2);
    if (value !== '' && value !== undefined) this.setFixedValue(name, val);
    switch (name) {
      case 'sideA':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.sideA = true;
          this.formValidation.formErrors.sideA = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, "Side 'a'");
        } else {
          this.formValidation.sideA = false;
          this.formValidation.formErrors.sideA = '';
          this.formValidation.hasError = false;
          if (this.rectangularControl === 1 && this.rectangularForm.sideB !== undefined) {
            this.rectangularForm.diagonal = this.roundingHelperString(Math.sqrt(Math.pow(value, 2) + Math.pow(parseFloat(this.rectangularForm.sideB), 2)), 2)
          } else if (this.rectangularControl === 0 && this.rectangularForm.diagonal !== undefined) this.rectangularForm.sideB = this.roundingHelperString(Math.sqrt(Math.pow(parseFloat(this.rectangularForm.diagonal), 2) - Math.pow(value, 2)), 2)
        }
        break;
      case 'sideB':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.sideB = true;
          this.formValidation.formErrors.sideB = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, "Side 'b'");
        }
        else {
          this.formValidation.sideB = false;
          this.formValidation.formErrors.sideB = '';
          this.formValidation.hasError = false;
          if (this.rectangularControl === 1 && this.rectangularForm.sideA !== undefined) {
            this.rectangularForm.diagonal = this.roundingHelperString(Math.sqrt(Math.pow(value, 2) + Math.pow(parseFloat(this.rectangularForm.sideA), 2)), 2)
          }
        }
        break;
      case 'diagonal':
        if (value === '' || val <= 0 || Number.isNaN(value)) {
          this.formValidation.diagonal = true;
          this.formValidation.formErrors.diagonal = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Diagonal')
        } else if (this.rectangularControl === 0 && this.rectangularForm.sideA !== undefined && value > this.rectangularForm.sideA) {
          this.rectangularForm.sideB = this.roundingHelperString(Math.sqrt(Math.pow(value, 2) -  Math.pow(parseFloat(this.rectangularForm.sideA), 2)), 2)
          this.formValidation.diagonal = false;
          this.formValidation.formErrors.diagonal = '';
          this.formValidation.hasError = false;
        } else if (this.rectangularControl === 0 && value <= this.rectangularForm.sideA) {
          this.rectangularForm.sideB = "0";
          this.formValidation.diagonal = true;
          this.formValidation.formErrors.diagonal = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, "Diagonal should be greater than side 'a' and")
        }
        break;
    }
  }

  get validateRequiredInputs() {
    if (!this.formValidation.diagonal && !this.formValidation.sideA && !this.formValidation.sideB) {
      return true
    }
    return false
  }

  @action async validateReqInput() {
    await this.validateInput("sideA", this.rectangularForm.sideA);
    if (this.rectangularControl === 1) {
      await this.validateInput("sideB", this.rectangularForm.sideB);
    }
    else {
      await this.validateInput("diagonal", this.rectangularForm.diagonal);
    }
  }

  @action  async computeRectangular() {
    this.disableComputeBtn = true;
    MessageCenter.blockCalls(['Common_26']);
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      this.disablePrintBtn = false;
      this.hasResults = true;
      this.isLoading = false;
      if (this.rectangularControl === 1) {
        this.rectangularResults.area = this.roundingHelperString((parseFloat(this.rectangularForm.sideA) * parseFloat(this.rectangularForm?.sideB)), 2)
      } else {
        this.rectangularResults.area = this.rectangularForm.diagonal ? 
        this.roundingHelperString((parseFloat(this.rectangularForm.sideA) * (Math.sqrt(Math.pow(parseFloat(this.rectangularForm.diagonal), 2) - Math.pow(parseFloat(this.rectangularForm.sideA), 2)))), 2) 
        : "";
      }
      this.rectangularFormInputSnapshot = { ...this.rectangularForm };
    } else {
      this.disablePrintBtn = true
      this.hasResults = false;
      MessageCenter.addNotificationFromCode('Common_26');
    }
    MessageCenter.unblockCalls();
    this.disableComputeBtn = false;
  }

  @action.bound launchAndExport() {
    // disable print button
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const input in this.rectangularFormInputSnapshot) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.rectangularFormInputSnapshot[input]
      })
    }
    for (const result in this.rectangularResults) {
      const label = this.resultLabelsForExport[result];
      const value = this.rectangularResults[result];
      if (label && value !== undefined) {
        this.exportedResults.push({
          label: this.resultLabelsForExport[result],
          value: this.rectangularResults[result]
        })
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.rectangularForm = {
      sideA: this.defaultValues.a !== undefined ? this.roundingHelperString(this.defaultValues.a, 2) : '0.00',
      sideB: this.defaultValues.b !== undefined ? this.roundingHelperString(this.defaultValues.b, 2) : '0.00',
      diagonal: this.defaultValues.diagonal !== undefined ? this.roundingHelperString(this.defaultValues.diagonal, 2) : '0.00',
    }
    this.rectangularFormInputSnapshot = { ...this.rectangularForm }
  }
}